import type { FC, PropsWithChildren } from 'react';
import React, { createContext, useContext } from 'react';
import { useFragment } from 'react-relay';

import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type {
	AudioContentFragment_content$data,
	AudioContentFragment_content$key,
} from './__generated__/AudioContentFragment_content.graphql.ts';
import { AudioContentFragment } from './AudioContentFragment.ts';

const AudioContentContext = createContext<AudioContentFragment_content$data | undefined>(undefined);

type AudioContentProviderProps = PropsWithChildren<{
	contentId: string;
	contentFragmentRef?: AudioContentFragment_content$key | null;
}>;

const AudioContentFragmentProvider: FC<AudioContentProviderProps> = ({
	children,
	contentId,
	contentFragmentRef,
}) => {
	const content = useFragment<AudioContentFragment_content$key>(
		AudioContentFragment,
		contentFragmentRef ?? null,
	);
	const audioContent = content?.id === contentId ? content : undefined;

	return (
		<AudioContentContext.Provider value={audioContent}>{children}</AudioContentContext.Provider>
	);
};

export const AudioContentProvider: FC<AudioContentProviderProps> = ({
	children,
	contentId,
	contentFragmentRef,
}) => {
	if (
		!contentFragmentRef ||
		StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') ||
		!expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return (
			<AudioContentContext.Provider value={undefined}>{children}</AudioContentContext.Provider>
		);
	}

	return (
		<AudioContentFragmentProvider contentId={contentId} contentFragmentRef={contentFragmentRef}>
			{children}
		</AudioContentFragmentProvider>
	);
};

export const useAudioContentFragmentData = (): AudioContentFragment_content$data | undefined =>
	useContext(AudioContentContext);
